/**
 * Shared store filtering, so the table and the timeline never drift apart on
 * what a search box accepts.
 */

import type { StoreRecord } from "./queries";

/**
 * Anything store-shaped enough to search. The retailer is optional so this
 * works on a bare store record as well as an inventory-joined one.
 */
export type SearchableStore = StoreRecord & {
  retailer?: { name: string; short_name: string } | null;
  active?: boolean | null;
};

/** Selectable store status. Absent/null means no status filter. */
export type StoreStatus = "active" | "inactive";

/** Human-readable fields, matched on a substring. */
function textFields(store: SearchableStore): (string | null | undefined)[] {
  return [
    store.name,
    store.site_id,
    store.slug,
    store.timezone,
    store.retailer?.name,
    store.retailer?.short_name,
  ];
}

/**
 * Short identifiers, matched exactly.
 *
 * `id` is what the detail page labels "Lebron ID". Substring-matching it would
 * make a query of `3` pull in stores 3, 13, 30 and 300 at once, so an ID has
 * to be given in full.
 */
function exactIdFields(store: SearchableStore): (string | null | undefined)[] {
  return [store.id, store.retailer_id];
}

/**
 * Long identifiers, matched on a prefix so a partial paste still works.
 *
 * A UUID contains most short digit pairs somewhere, so substring-matching it
 * would make a query like `34` hit unrelated stores whose Atlas ID merely
 * happens to contain those characters.
 */
function prefixIdFields(store: SearchableStore): (string | null | undefined)[] {
  return [store.atlas_id];
}

/** Below this, a prefix is too short to be a meaningful UUID fragment. */
const MIN_PREFIX_LENGTH = 4;

function termMatches(store: SearchableStore, term: string): boolean {
  if (textFields(store).some((value) => value?.toLowerCase().includes(term))) {
    return true;
  }
  if (exactIdFields(store).some((value) => value?.toLowerCase() === term)) {
    return true;
  }
  if (term.length >= MIN_PREFIX_LENGTH) {
    return prefixIdFields(store).some((value) =>
      value?.toLowerCase().startsWith(term),
    );
  }
  return false;
}

/**
 * Whether a store matches a free-text query.
 *
 * Accepts a store name, slug, timezone, retailer name or short name, site ID
 * (`S_34`), Lebron ID (`34`), retailer ID, or an Atlas UUID — whole or by its
 * leading characters.
 *
 * Several whitespace-separated terms must all match, though not necessarily in
 * the same field, so `chemist brentford` finds the Brentford store and a
 * pasted `" S_34 "` behaves like `S_34`.
 */
export function storeMatches(store: SearchableStore, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  return terms.every((term) => termMatches(store, term));
}

export interface StoreFilters {
  search: string;
  /** Exact IANA zone, or null for no timezone filter. */
  timezone: string | null;
  /** Retailer names to keep. Empty means every retailer. */
  retailers?: string[];
  /** Keep only active or only inactive stores. Null means both. */
  status?: StoreStatus | null;
}

export function filterStores<T extends SearchableStore>(
  stores: T[],
  { search, timezone, retailers = [], status = null }: StoreFilters,
): T[] {
  const wanted = new Set(retailers);

  return stores.filter((store) => {
    if (timezone && store.timezone !== timezone) return false;

    // A store with no resolved retailer is excluded once a retailer is chosen,
    // rather than leaking into every selection.
    if (wanted.size > 0 && !(store.retailer && wanted.has(store.retailer.name)))
      return false;

    // `active` is null when inventory did not resolve. An unknown status is
    // neither active nor inactive, so it is excluded by either choice rather
    // than being guessed one way.
    if (status === "active" && store.active !== true) return false;
    if (status === "inactive" && store.active !== false) return false;

    return storeMatches(store, search);
  });
}

/** Placeholder text kept in one place so both search boxes agree. */
export const STORE_SEARCH_PLACEHOLDER = "Name, ID, retailer or timezone";
